import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, AlertTriangle, Info, Users, Layers, Sun } from "lucide-react";
import type { MoveWarning } from "@/lib/reservationMoveWarnings";

interface PendingChange {
  targetName: string;
  targetCapacity: number;
  targetIsCombined: boolean;
  targetIsTerrace?: boolean;
}

interface ChangeTableConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reservation: {
    customerName: string;
    persons: number;
    time: string;
    currentTableName: string;
  };
  pending: PendingChange | null;
  warnings: MoveWarning[];
  onConfirm: () => void;
  isSubmitting?: boolean;
}

/**
 * Confirmation dialog shown before applying a table change from the preview.
 * Surfaces soft warnings (capacity, terrace preference, min capacity) so the
 * user explicitly accepts them. Blocking warnings disable the confirm button.
 */
export const ChangeTableConfirmDialog = ({
  open,
  onOpenChange,
  reservation,
  pending,
  warnings,
  onConfirm,
  isSubmitting,
}: ChangeTableConfirmDialogProps) => {
  if (!pending) return null;
  const blocking = warnings.some((w) => w.level === "block");

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>Confirmer le changement de table</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 pt-1 text-sm">
              <div className="rounded-md border bg-muted/30 p-3 space-y-2">
                <div className="font-medium text-foreground">
                  {reservation.customerName}
                </div>
                <div className="text-xs text-muted-foreground flex items-center gap-2">
                  <Users className="w-3 h-3" /> {reservation.persons} pers. · {reservation.time}
                </div>
                <div className="flex items-center justify-between gap-2 pt-1">
                  <span className="text-foreground">{reservation.currentTableName}</span>
                  <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span className="font-medium text-foreground flex items-center gap-1">
                    {pending.targetIsCombined && <Layers className="w-3 h-3" />}
                    {pending.targetIsTerrace && <Sun className="w-3 h-3 text-amber-500" />}
                    {pending.targetName}
                    <Badge variant="secondary" className="ml-1 text-[10px]">
                      {pending.targetCapacity} pers.
                    </Badge>
                  </span>
                </div>
              </div>

              {warnings.length > 0 && (
                <div className="space-y-1.5">
                  {warnings.map((w, i) => (
                    <WarningRow key={i} warning={w} />
                  ))}
                </div>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isSubmitting}>Annuler</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              if (blocking || isSubmitting) return;
              onConfirm();
            }}
            disabled={blocking || isSubmitting}
          >
            {blocking ? "Action bloquée" : "Confirmer le changement"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const WarningRow = ({ warning }: { warning: MoveWarning }) => {
  const styles =
    warning.level === "block"
      ? "border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-300"
      : warning.level === "warn"
        ? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300"
        : "border-blue-500/40 bg-blue-500/10 text-blue-700 dark:text-blue-300";
  const Icon = warning.level === "info" ? Info : AlertTriangle;
  return (
    <div className={`flex items-start gap-2 rounded-md border px-2.5 py-1.5 text-xs ${styles}`}>
      <Icon className="w-3.5 h-3.5 mt-0.5 shrink-0" />
      <span>{warning.message}</span>
    </div>
  );
};
